import { useRef, useState } from "react";
import { Footer } from "@/components/Footer";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, FileUp, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Layout";
import { Spinner } from "@/components/Common";
import { SkillsInput } from "@/components/SkillsInput";
import { useAuth, profileComplete } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

export default function Onboarding() {
  const { profile, refreshProfile, user, logout } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    headline: profile?.headline || "", location: profile?.location || "",
    skills: profile?.skills || [], resume_text: profile?.resume_text || "",
  });
  const [resumeFile, setResumeFile] = useState(profile?.resume_file || null);
  const [busy, setBusy] = useState("");

  const autofill = async () => {
    setBusy("autofill");
    try {
      const { data } = await api.post("/profile/autofill");
      setForm((f) => ({ ...f, headline: data.headline || f.headline, location: data.location || f.location, skills: data.skills?.length ? data.skills : f.skills }));
      toast.success("Profile prefilled from your resume");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const upload = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") return toast.error("Please upload a PDF");
    if (file.size > 5 * 1024 * 1024) return toast.error("PDF must be 5MB or smaller");
    setBusy("upload");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/profile/resume", fd);
      const p = await refreshProfile();
      setResumeFile(p.resume_file || { name: file.name });
      if (p.resume_text) setForm((f) => ({ ...f, resume_text: p.resume_text }));
      toast.success("Resume uploaded");
      await autofill();
    } catch (e) { toast.error(errMsg(e)); setBusy(""); }
  };

  const candidate = { ...form, resume_file: resumeFile };
  const ready = profileComplete(candidate);

  const save = async () => {
    setBusy("save");
    try {
      await api.put("/profile", form);
      const p = await refreshProfile();
      if (profileComplete(p)) { const back = sessionStorage.getItem("rpopro_return"); navigate(back?.startsWith("/") ? back : "/search", { replace: true }); }
      else toast.error("Profile still incomplete: resume, headline and one skill are required");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  return (
    <div className="grain min-h-screen bg-[#09090B]">
      <header className="relative z-[2] flex items-center justify-between px-6 py-5 md:px-12">
        <Logo />
        <button onClick={logout} className="text-sm text-zinc-500 hover:text-zinc-300" data-testid="onboarding-logout">Log out</button>
      </header>
      <div className="relative z-[2] mx-auto max-w-3xl px-6 pb-20 pt-6">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Setup · 2 minutes</p>
        <h1 className="mt-3 font-display text-4xl font-semibold tracking-tight text-zinc-100 sm:text-5xl">Welcome, {user?.name?.split(" ")[0]}.</h1>
        <p className="mt-3 text-zinc-400">Your resume powers matching, cover letters and the auto-apply agent. Upload it once, it syncs with the mobile app.</p>

        <section className="mt-10 rounded-2xl border border-[#27272A] bg-[#18181B] p-6 fade-up">
          <div className="mb-4 flex items-center gap-3"><span className="font-mono text-xs text-[#D4AF37]">01</span><h2 className="font-display text-lg text-zinc-100">Upload your resume</h2></div>
          <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="resume-file-input" />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={!!busy} data-testid="resume-upload-button"
            onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files?.[0]); }}
            className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[#3F3F46] bg-[#09090B] px-6 py-10 transition-colors hover:border-[#D4AF37]/60">
            {busy === "upload" ? <Spinner className="h-6 w-6 text-[#D4AF37]" /> : resumeFile ? <CheckCircle2 className="h-6 w-6 text-emerald-400" /> : <FileUp className="h-6 w-6 text-[#D4AF37]" />}
            <span className="text-sm text-zinc-300" data-testid="resume-file-name">{resumeFile ? resumeFile.name || "Resume on file" : "Drop a PDF here or click to browse"}</span>
            <span className="text-xs text-zinc-600">PDF, up to 5MB</span>
          </button>
          <details className="mt-4">
            <summary className="cursor-pointer text-xs text-zinc-500">Or paste resume text instead (200+ characters)</summary>
            <Textarea rows={6} value={form.resume_text} onChange={(e) => setForm({ ...form, resume_text: e.target.value })} className="mt-3 bg-[#09090B]" data-testid="resume-text-input" />
          </details>
        </section>

        <section className="mt-6 rounded-2xl border border-[#27272A] bg-[#18181B] p-6 fade-up" style={{ animationDelay: "100ms" }}>
          <div className="mb-5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3"><span className="font-mono text-xs text-[#D4AF37]">02</span><h2 className="font-display text-lg text-zinc-100">Your profile</h2></div>
            <Button variant="outline" size="sm" onClick={autofill} disabled={!!busy || (!resumeFile && form.resume_text.length < 200)} data-testid="autofill-button">
              {busy === "autofill" ? <Spinner /> : <Sparkles className="h-4 w-4 text-[#D4AF37]" />} Autofill with AI
            </Button>
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            <div className="space-y-2"><Label>Headline</Label><Input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} placeholder="Senior Product Designer" className="bg-[#09090B]" data-testid="headline-input" /></div>
            <div className="space-y-2"><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="New York, NY" className="bg-[#09090B]" data-testid="location-input" /></div>
            <div className="space-y-2 md:col-span-2"><Label>Skills</Label><SkillsInput value={form.skills} onChange={(skills) => setForm({ ...form, skills })} /></div>
          </div>
        </section>

        <Button onClick={save} disabled={!ready || !!busy} className="mt-8 h-12 w-full rounded-full text-sm font-semibold" data-testid="onboarding-save-button">
          {busy === "save" ? <Spinner /> : "Launch my command center"}
        </Button>
        {!ready && <p className="mt-3 text-center text-xs text-zinc-500">Required: a resume, a headline and at least one skill.</p>}
      </div>
      <Footer />
    </div>
  );
}
